import React, { useEffect } from "react";
import Lenis from "lenis";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { scrollToId } from "@/lib/motion";
import { Toaster } from "sonner";
import "@/App.css";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import TrustMarquee from "@/components/TrustMarquee";
import ProcessSteps from "@/components/ProcessSteps";
import LogoWall from "@/components/LogoWall";
import Services from "@/components/Services";
import CaseStudies from "@/components/CaseStudies";
import Founder from "@/components/Founder";
import Testimonials from "@/components/Testimonials";
import SeoSnapshot from "@/components/SeoSnapshot";
import Booking from "@/components/Booking";
import Footer from "@/components/Footer";
import AskTodd from "@/components/AskTodd";
import Seo from "@/components/Seo";
import ServicePage from "@/pages/ServicePage";
import AboutPage from "@/pages/AboutPage";
import FaqPage from "@/pages/FaqPage";
import ToddPage from "@/pages/ToddPage";
import ContactPage from "@/pages/ContactPage";
import TestimonialsPage from "@/pages/TestimonialsPage";
import CaseStudyPage from "@/pages/CaseStudyPage";
import CaseStudiesPage from "@/pages/CaseStudiesPage";
import CrossWebRepairPage from "@/pages/CrossWebRepairPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import BlogArticlePage from "@/pages/BlogArticlePage";
import BlogSection from "@/components/BlogSection";
import { ARTICLES, LEGACY_SLUGS } from "@/data/blogArticles";
import { SERVICES } from "@/data/services";
import PrivacyPage from "@/pages/PrivacyPage";
import AiVisibilityPage from "@/pages/AiVisibilityPage";
import AiSearchVisibilityPage from "@/pages/AiSearchVisibilityPage";
import ThankYouPage from "@/pages/ThankYouPage";
import SitemapPage from "@/pages/SitemapPage";
import StrategyCallPage from "@/pages/StrategyCallPage";
import NotFoundPage from "@/pages/NotFoundPage";
import AiAnswerChecklistPage from "@/pages/AiAnswerChecklistPage";
import PackagesPage from "@/pages/PackagesPage";
import LeadsDashboard from "@/pages/LeadsDashboard";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const t1 = setTimeout(() => scrollToId(id), 600);
      const t2 = setTimeout(() => scrollToId(id), 1600);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return null;
}

function Home() {
  return (
    <>
      <Seo
        title="ZD Design — Small Business Marketing & Lead Generation"
        description="ZD Design helps established businesses improve their websites, Google visibility and advertising to generate more calls, inquiries and sales. Book a free call."
        path="/"
      />
      <main>
        <Hero />
        <LogoWall />
        <SeoSnapshot />
        <TrustMarquee />
        <Services />
        <Founder />
        <ProcessSteps />
        <CaseStudies />
        <Testimonials />
        <Booking highLevel />
        <Marquee />
        <BlogSection />
      </main>
    </>
  );
}

function BlogEntry() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);
  return article ? <BlogArticlePage article={article} /> : <BlogPostPage />;
}

function RootArticle() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug && LEGACY_SLUGS.has(a.slug));
  if (article) return <BlogArticlePage article={article} />;
  const service = SERVICES.find((s) => s.route === `/${slug}` && s.slug !== "cross-web-repair" && s.slug !== "ai-search-visibility");
  if (service) return <ServicePage slugOverride={service.slug} />;
  return <NotFoundPage />;
}

function Shell() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/leads")) return <LeadsDashboard />;
  return (
    <>
      <Nav />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/todd" element={<ToddPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/testimonials" element={<TestimonialsPage />} />
        <Route path="/case-studies" element={<CaseStudiesPage />} />
        <Route path="/cross-web-repair" element={<CrossWebRepairPage />} />
        <Route path="/ai-search-visibility" element={<AiSearchVisibilityPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/sitemap" element={<SitemapPage />} />
        <Route path="/ai-visibility" element={<AiVisibilityPage />} />
        <Route path="/packages" element={<PackagesPage />} />
        <Route path="/free-strategy-call" element={<StrategyCallPage />} />
        <Route path="/thank-you" element={<ThankYouPage />} />
        <Route path="/case-studies/:slug" element={<CaseStudyPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/ai-answer-checklist" element={<AiAnswerChecklistPage />} />
        <Route path="/blog/:slug" element={<BlogEntry />} />
        <Route path="/:slug" element={<RootArticle />} />
        {SERVICES.filter((s) => s.route).map((s) => (
          <Route
            key={s.slug}
            path={`/services/${s.slug}`}
            element={<Navigate to={s.route} replace />}
          />
        ))}
        <Route
          path="/case-studies/millers-heating-air"
          element={<Navigate to="/case-studies/vancouver-heating-and-air" replace />}
        />
        <Route
          path="/case-studies/stoney-degeyter"
          element={<Navigate to="/testimonials" replace />}
        />
        {[...LEGACY_SLUGS].map((slug) => (
          <Route
            key={slug}
            path={`/blog/${slug}`}
            element={<Navigate to={`/${slug}`} replace />}
          />
        ))}
        {[
          ["https-securing-your-website", "/5-reasons-to-update-your-wordpress-website"],
          ["what-role-does-content-play-in-seo", "/fresh-content"],
          ["tip-1-7-email-opt-tips-improve-conversions", "/7-email-capture-opt-tips-improve-conversions"],
          ["tip-2-7-email-opt-tips-improve-conversions", "/7-email-capture-opt-tips-improve-conversions"],
          ["tip-3-7-email-opt-tips-improve-conversions", "/7-email-capture-opt-tips-improve-conversions"],
          ["tip-4-7-email-opt-tips-improve-conversions", "/7-email-capture-opt-tips-improve-conversions"],
          ["tip-5-7-email-opt-tips-improve-conversions", "/7-email-capture-opt-tips-improve-conversions"],
          ["tip-6-7-email-opt-tips-improve-conversions", "/7-email-capture-opt-tips-improve-conversions"],
          ["7-email-opt-tips-improve-conversions-tip-7", "/7-email-capture-opt-tips-improve-conversions"],
          ["3-tips-for-designing-a-mobile-website", "/is-your-website-mobile-ready-why-your-mobile-site-needs-to-be-optimized-for-conversion"],
          ["6-tips-for-designing-for-responsive-layouts", "/is-your-website-mobile-ready-why-your-mobile-site-needs-to-be-optimized-for-conversion"],
          ["boost-sales-with-a-mobile-version", "/is-your-website-mobile-ready-why-your-mobile-site-needs-to-be-optimized-for-conversion"],
          ["6-steps-to-creating-a-social-media-plan", "/how-to-use-facebook-custom-audiences-in-power-editor-for-ad-targeting"],
          ["14-ways-to-define-your-social-media-audience", "/how-to-use-facebook-custom-audiences-in-power-editor-for-ad-targeting"],
        ].map(([from, to]) => (
          <Route
            key={from}
            path={`/${from}`}
            element={<Navigate to={to} replace />}
          />
        ))}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Footer />
      <AskTodd />
    </>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-white px-6 text-center font-sans text-slate-700">
          Something went wrong. Please refresh the page.
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <ScrollToTop />
          <SmoothScroll />
          <Shell />
          <Toaster
            theme="dark"
            position="bottom-right"
            toastOptions={{
              style: {
                background: "rgba(18,20,29,0.9)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#F8FAFC",
              },
            }}
          />
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
